import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PhotoIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import {
  getPublicationPreviewImage,
  resolveMediaUrl,
} from '../utils/media';
import MediaImage from '../components/MediaImage';
import {
  NewsCategory,
  newsCategoryLabelMap,
  normalizeNewsCategory,
} from '../constants/newsCategories';
import { compareDescByDate, formatChinaDate } from '../utils/date';

type HomeNewsItem = {
  _id: string;
  title: string;
  summary: string;
  category: NewsCategory;
  publishedAt?: string;
  isFeatured?: boolean;
  coverUrl?: string;
};

type HomePublication = {
  _id: string;
  title: string;
  authors: string;
  venue?: string;
  abstract?: string;
  content?: string;
  year?: number;
  url?: string;
  createdAt?: string;
  isHighlighted?: boolean;
  order?: number;
};

type ResearchDirection = {
  _id?: string;
  order: number;
  title: string;
  summary: string;
  description: string;
  bullets: string[];
};

type ResearchPageData = {
  intro: string;
  directions: ResearchDirection[];
};

type HomeHeroConfig = {
  kicker: string;
  heading: string;
  description: string;
  primaryButtonLabel: string;
  primaryButtonLink: string;
  secondaryButtonLabel: string;
  secondaryButtonLink: string;
  images: string[];
};

const renderHtml = (html?: string) => ({ __html: html || '' });
/** Plain "CO2" in hero copy → CO₂ for HTML display (skips strings that already use <sub>). */
const formatHeroDescriptionHtml = (html: string) =>
  html.replace(/\bCO2\b/g, 'CO<sub>2</sub>');
const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const unwrapParagraph = (value: string = '') =>
  value
    .trim()
    .replace(/^<p>([\s\S]*)<\/p>$/i, '$1')
    .trim();

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<HomeNewsItem[]>([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [featuredPubs, setFeaturedPubs] = useState<HomePublication[]>([]);
  const [loadingFeaturedPubs, setLoadingFeaturedPubs] = useState(true);
  const [researchData, setResearchData] = useState<ResearchPageData | null>(null);
  const [loadingResearch, setLoadingResearch] = useState(true);
  const [heroConfig, setHeroConfig] = useState<HomeHeroConfig | null>(null);
  const [heroImages, setHeroImages] = useState<string[]>([]);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingNews(true);
        const res = await api.get<HomeNewsItem[]>('/news');
        setNews(
          (res.data || []).map((item) => ({
            ...item,
            category: normalizeNewsCategory(item.category),
          }))
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingNews(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingFeaturedPubs(true);
        const res = await api.get<HomePublication[]>(
          '/publications?highlighted=true'
        );
        const sorted = (res.data || []).slice().sort((a, b) => {
          const orderDiff = (a.order ?? 0) - (b.order ?? 0);
          if (orderDiff !== 0) return orderDiff;
          return (b.year ?? 0) - (a.year ?? 0);
        });
        setFeaturedPubs(sorted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingFeaturedPubs(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingResearch(true);
        const res = await api.get<ResearchPageData>('/research-page');
        setResearchData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingResearch(false);
      }
    };
    load();
  }, []);

  // Homepage hero config
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get<HomeHeroConfig>('/home-hero');
        const cfg = res.data || ({} as HomeHeroConfig);
        const images =
          Array.isArray(cfg.images) && cfg.images.length > 0
            ? cfg.images.map((u) => resolveMediaUrl(u) || u)
            : ['/hero-lab.jpg'];
        setHeroConfig(cfg);
        setHeroImages(images);
        setHeroIndex(0);
      } catch (err) {
        console.error(err);
        setHeroConfig(null);
        setHeroImages(['/hero-lab.jpg']);
        setHeroIndex(0);
      }
    };
    load();
  }, []);

  // Simple carousel when multiple images exist
  useEffect(() => {
    if (heroImages.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [heroImages]);

  const latest = useMemo(
    () =>
      news
        .slice()
        .sort(
          (a, b) =>
            compareDescByDate(a.publishedAt, b.publishedAt)
        )
        .slice(0, 3),
    [news]
  );

  const domains = useMemo(
    () => (researchData?.directions || []).slice(0, 4),
    [researchData]
  );

  const currentHeroImage = heroImages[heroIndex] || '/hero-lab.jpg';
  const kicker =
    heroConfig?.kicker && heroConfig.kicker.trim()
      ? heroConfig.kicker
      : "Prof. Hui Pan's Research Group";
  const heading =
    heroConfig?.heading && heroConfig.heading.trim()
      ? heroConfig.heading
      : 'Catalysis for Green Energy';
  const description =
    heroConfig?.description && heroConfig.description.trim()
      ? heroConfig.description
      : 'Research topics include electrochemical CO<sub>2</sub> reduction, water electrolysis, catalyst/interface engineering, and scalable catalytic technologies.';
  const primaryLabel =
    heroConfig?.primaryButtonLabel && heroConfig.primaryButtonLabel.trim()
      ? heroConfig.primaryButtonLabel
      : 'Explore Research';
  const primaryLink =
    heroConfig?.primaryButtonLink && heroConfig.primaryButtonLink.trim()
      ? heroConfig.primaryButtonLink
      : '/research';
  const secondaryLabel =
    heroConfig?.secondaryButtonLabel &&
    heroConfig.secondaryButtonLabel.trim()
      ? heroConfig.secondaryButtonLabel
      : 'Meet the Team';
  const secondaryLink =
    heroConfig?.secondaryButtonLink &&
    heroConfig.secondaryButtonLink.trim()
      ? heroConfig.secondaryButtonLink
      : '/members';

  return (
    <>
      <section className="relative flex min-h-[640px] items-start overflow-hidden bg-surface-container-low">
        <div className="absolute inset-0 z-0">
          <MediaImage
            src={currentHeroImage}
            alt="Lab environment and materials research"
            className="h-full w-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface/90 to-surface/30" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-screen-xl flex-col gap-10 px-6 py-12 md:px-8 md:py-16 lg:flex-row lg:items-start">
          <div className="w-full space-y-6 lg:w-full">
            <span className="inline-block rounded-full bg-secondary-container px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-on-secondary-container">
              {kicker}
            </span>
            <h1 className="font-serif text-5xl leading-tight text-on-surface md:text-6xl lg:text-7xl">
              {heading}
            </h1>
            <p
              className="max-w-2xl text-lg leading-relaxed text-on-surface-variant"
              dangerouslySetInnerHTML={renderHtml(
                formatHeroDescriptionHtml(description)
              )}
            />
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate(primaryLink)}
                className="um-btn-primary px-8 py-3.5 text-sm tracking-wide"
              >
                {primaryLabel}
              </button>
              <button
                onClick={() => navigate(secondaryLink)}
                className="um-btn-secondary px-8 py-3.5 text-sm tracking-wide"
              >
                {secondaryLabel}
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-primary py-3 text-white">
        <div className="mx-auto flex max-w-screen-2xl items-center gap-3 px-8 text-sm">
          <span className="font-semibold">Latest Updates:</span>
          <p className="truncate">
            We welcome applications from students and postdoctoral researchers in applied physics, materials, chemistry, and related interdisciplinary fields.
          </p>
        </div>
      </div>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-screen-xl px-8">
          <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-primary text-sm font-semibold uppercase tracking-[0.2em]">
                Featured Work
              </p>
              <h2 className="mt-2 font-serif text-4xl text-on-surface">
                Representative Publications
              </h2>
            </div>
            <Link to="/publications" className="um-btn-link shrink-0">
              View all publications <span>→</span>
            </Link>
          </div>

          {loadingFeaturedPubs ? (
            <p className="text-on-surface-variant">Loading...</p>
          ) : featuredPubs.length === 0 ? (
            <p className="text-on-surface-variant">
              No representative publications yet. Mark items as featured in the admin
              panel.
            </p>
          ) : (
            <div className="flex flex-col gap-12">
              {featuredPubs.map((pub) => {
                const previewImage = getPublicationPreviewImage(pub);
                return (
                  <article
                    key={pub._id}
                    className="flex flex-col gap-6 border-b border-outline-variant/20 pb-12 last:border-b-0 last:pb-0 md:flex-row md:items-start"
                  >
                    <Link
                      to={`/publications/${pub._id}`}
                      className="relative block shrink-0 md:w-2/5"
                    >
                      <div className="absolute -inset-3 -z-10 rounded-xl bg-surface-container-low -rotate-1" />
                      {previewImage ? (
                        <MediaImage
                          src={previewImage}
                          alt={stripHtml(pub.title)}
                          className="aspect-video w-full rounded-lg object-cover"
                        />
                      ) : (
                        <div
                          className="flex aspect-video w-full items-center justify-center rounded-lg bg-surface-container"
                          aria-hidden
                        >
                          <PhotoIcon className="h-12 w-12 text-outline-variant/60" />
                        </div>
                      )}
                    </Link>
                    <div className="flex flex-1 flex-col justify-center gap-3 md:w-3/5">
                      {pub.year && (
                        <span className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                          {pub.year}
                          {pub.venue ? ` · ${stripHtml(pub.venue)}` : ''}
                        </span>
                      )}
                      <Link to={`/publications/${pub._id}`}>
                        <h3
                          className="font-serif text-2xl leading-snug text-on-surface transition-colors hover:text-primary md:text-3xl"
                          dangerouslySetInnerHTML={renderHtml(pub.title)}
                        />
                      </Link>
                      <p className="text-base leading-relaxed text-on-surface-variant">
                        {stripHtml(pub.authors)}
                      </p>
                      <Link
                        to={`/publications/${pub._id}`}
                        className="um-btn-link mt-1 w-fit text-sm"
                      >
                        Read publication <span>→</span>
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="bg-surface-container-low py-24">
        <div className="mx-auto max-w-screen-xl px-8">
          <div className="mb-14 md:w-2/3">
            <h2 className="font-serif text-4xl text-on-surface mb-3">Research Directions</h2>
            <p className="text-lg text-on-surface-variant">
              We focus on the intersection of materials, devices, and physics to advance key scientific questions and engineering applications.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {loadingResearch ? (
              <p className="col-span-3 text-center text-on-surface-variant">Loading...</p>
            ) : domains.length === 0 ? (
              <p className="col-span-3 text-center text-on-surface-variant">No research directions yet. Please configure them in the admin panel.</p>
            ) : (
              domains.map((d, index) => (
                <div
                  key={d._id ?? index}
                  className={`ambient-shadow rounded-lg p-7 ${
                    index === 0 ? 'md:col-span-2 bg-surface-container-lowest' : 'bg-surface-container-lowest'
                  }`}
                >
                  <div className="mb-3 text-xs uppercase tracking-[0.2em] text-on-surface-variant font-semibold">
                    Direction {index + 1}
                  </div>
                  <h3 className="mb-2 font-serif text-2xl text-on-surface">
                    <span
                      dangerouslySetInnerHTML={renderHtml(d.title || 'Untitled direction')}
                    />
                  </h3>
                  <p
                    className="text-on-surface-variant"
                    dangerouslySetInnerHTML={renderHtml(
                      unwrapParagraph(d.summary || d.description || '-')
                    )}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-screen-xl px-8">
          <div className="mb-12 flex items-end justify-between">
            <h2 className="font-serif text-4xl text-on-surface">News</h2>
            <Link to="/news" className="um-btn-link">
              View all
            </Link>
          </div>
          {loadingNews ? (
            <p className="text-on-surface-variant">Loading...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latest.map((item) => (
                <article key={item._id} className="group">
                  <Link
                    to={`/news/${item._id}`}
                    className="mb-5 block aspect-video overflow-hidden rounded-lg bg-surface-container"
                  >
                    {resolveMediaUrl(item.coverUrl) ? (
                      <MediaImage
                        src={item.coverUrl}
                        alt={stripHtml(item.title)}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div
                        className="flex h-full w-full items-center justify-center bg-surface-container"
                        aria-hidden
                      >
                        <PhotoIcon className="h-12 w-12 text-outline-variant/50" />
                      </div>
                    )}
                  </Link>
                  <div className="mb-3 flex items-center gap-3 text-xs text-on-surface-variant">
                    <span>{item.publishedAt ? formatChinaDate(item.publishedAt) : ''}</span>
                    <span className="h-1 w-1 rounded-full bg-outline-variant" />
                    <span className="rounded bg-surface-container-high px-2 py-1 uppercase tracking-wide">
                      {newsCategoryLabelMap[item.category]}
                    </span>
                  </div>
                  <Link to={`/news/${item._id}`} className="font-serif text-2xl text-on-surface group-hover:text-primary transition-colors">
                    <span dangerouslySetInnerHTML={renderHtml(item.title)} />
                  </Link>
                  <p className="mt-2 line-clamp-2 text-on-surface-variant">
                    {stripHtml(item.summary)}
                  </p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Home;
